import {
    getRecordProcessStageKey,
    normalizeProcessUserType,
} from './processStageAccess'

export const RESTRICTED_PROCESS_STAGE_KEY = '__restricted__'
export const UNKNOWN_PROCESS_STAGE_KEY = '__unknown__'

export const RESTRICTED_PROCESS_STAGE = {
    key: RESTRICTED_PROCESS_STAGE_KEY,
    name: '{{lang}}Restricted stages',
    color: '#94A3B8',
    restricted: true,
}

export const UNKNOWN_PROCESS_STAGE = {
    key: UNKNOWN_PROCESS_STAGE_KEY,
    name: '{{lang}}Not started',
    color: '#CBD5E1',
    unknown: true,
}

const getStableRecordNumber = record => {
    const id = String(record?._id || record?.values?.formId || '')
    const match = id.match(/(\d+)(?!.*\d)/)
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER
}

const getNestedRecordValue = (record, key) => {
    return String(key).split('.').reduce((value, segment) => {
        return value == null ? undefined : value[segment]
    }, record)
}

const decoratePreviewRelationships = record => {
    const values = { ...record.values }

    if (values.veterinarian && typeof values.veterinarian !== 'object') {
        values.veterinarian = {
            _id: values.veterinarian,
            name: `Veterinarian ${String(values.veterinarian).replace(/\D/g, '') || ''}`.trim(),
        }
    }
    if (values.customer && typeof values.customer !== 'object') {
        values.customer = {
            _id: values.customer,
            name: values.customerName || 'Preview customer',
        }
    }

    return values
}

export const getAdminWidgetPreviewRecords = ({
    records = [],
    limit = 10,
    sort = {},
}) => {
    const sortEntries = Object.entries(sort)
    const sortedRecords = records.slice()

    if (sortEntries.length > 0) {
        sortedRecords.sort((left, right) => {
            for (const [key, direction] of sortEntries) {
                const leftValue = getNestedRecordValue(left, key)
                const rightValue = getNestedRecordValue(right, key)
                if (leftValue === rightValue) continue
                return (leftValue > rightValue ? 1 : -1) * direction
            }
            return getStableRecordNumber(left) - getStableRecordNumber(right)
        })
    } else {
        sortedRecords.sort((left, right) => {
            return getStableRecordNumber(left) - getStableRecordNumber(right)
        })
    }

    return sortedRecords.slice(0, Math.max(0, limit)).map(record => ({
        ...record,
        values: decoratePreviewRelationships(record),
    }))
}

/**
 * Build a small, repeatable user-dashboard sample from the shared fake forms.
 * The two user roles intentionally receive different records, and each cloned
 * row is scoped to the local preview user's owner field.
 */
export const getUserWidgetPreviewRecords = ({
    records = [],
    userType,
    userId,
    limit = 10,
}) => {
    const normalizedUserType = normalizeProcessUserType(userType)
    const roleSettings = {
        veterinarian: { ownerKey: 'veterinarian', parity: 0 },
        horseBreeder: { ownerKey: 'customer', parity: 1 },
    }
    const settings = roleSettings[normalizedUserType]

    if (!settings) return []

    const candidates = records
        .filter(record => getRecordProcessStageKey(record))
        .slice()
        .sort((left, right) => getStableRecordNumber(left) - getStableRecordNumber(right))
    const roleCandidates = candidates.filter(record => {
        return getStableRecordNumber(record) % 2 === settings.parity
    })
    const selected = roleCandidates.slice(0, Math.max(0, limit))
    let veterinarianFixture

    if (normalizedUserType === 'veterinarian') {
        veterinarianFixture = candidates.find(record => {
            return record.__previewFixtureRole === 'veterinarian'
        }) || candidates.find(record => {
            return getRecordProcessStageKey(record) === 'coordination'
        })
        const includesFixture = selected.some(record => {
            return record === veterinarianFixture
        })

        if (veterinarianFixture && !includesFixture && limit > 0) {
            if (selected.length >= limit) {
                selected[selected.length - 1] = veterinarianFixture
            } else {
                selected.push(veterinarianFixture)
            }
        }
    }

    return selected.map(record => ({
        ...record,
        ...(record === veterinarianFixture
            ? { __previewFixtureRole: 'veterinarian' }
            : {}),
        values: {
            ...decoratePreviewRelationships(record),
            [settings.ownerKey]: userId || `local-preview-${normalizedUserType}`,
        },
    }))
}

/**
 * Count records without exposing stage names that are hidden for the viewer.
 */
export const summarizeProcessRecords = (records = [], stages = {}, userType) => {
    const normalizedUserType = normalizeProcessUserType(userType)
    const visibleEntries = Object.entries(stages).filter(([, stage]) => {
        return !Array.isArray(stage.visibleFor)
            || stage.visibleFor.includes(normalizedUserType)
    })
    const visibleStageKeys = new Set(visibleEntries.map(([key]) => key))
    const allStageKeys = new Set(Object.keys(stages))
    const counts = Object.fromEntries(visibleEntries.map(([key]) => [key, 0]))

    let restrictedCount = 0
    let unknownCount = 0

    records.forEach(record => {
        const stageKey = getRecordProcessStageKey(record)

        if (stageKey && visibleStageKeys.has(stageKey)) {
            counts[stageKey] += 1
        } else if (stageKey && allStageKeys.has(stageKey)) {
            restrictedCount += 1
        } else {
            unknownCount += 1
        }
    })

    if (restrictedCount > 0) {
        counts[RESTRICTED_PROCESS_STAGE_KEY] = restrictedCount
    }
    if (unknownCount > 0) {
        counts[UNKNOWN_PROCESS_STAGE_KEY] = unknownCount
    }

    return {
        counts,
        entries: [
            ...visibleEntries,
            ...(restrictedCount > 0
                ? [[RESTRICTED_PROCESS_STAGE_KEY, RESTRICTED_PROCESS_STAGE]]
                : []),
            ...(unknownCount > 0
                ? [[UNKNOWN_PROCESS_STAGE_KEY, UNKNOWN_PROCESS_STAGE]]
                : []),
        ],
        restrictedCount,
        unknownCount,
        totalCount: records.length,
    }
}
